import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";

// Hooks
import { usePayPlan } from "@/PayPlans/context/PayPlans";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import ModelBased from "./ModelBased";

// MUI
import { Grid } from "@mui/material";

export default function ModelBasedCommissions(props) {
	const {
		sectionId = "modelBased",
		commissions = null,
		frontProducts = [],
		backProducts = [],
		makes = [],
		tabsProps = {
			tabs: ["New Vehicles", "Pre-Owned Vehicles"],
			errors: [],
			globalUnitCounts: false,
			applyTiersRetroactively: false,
		},
		frontProductLabel = "Total Gross Profit",
		backProductLabel = "Total Gross Profit",
		emptyError = {
			error: false,
			emptyMessage: "Please add one or more model-based commissions.",
		},
		loading = false,
		loadingProducts = false,
		loadingMakes = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { useSectionsErrors } = usePayPlan();
	const { getErrorKey, addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors();

	// States
	const [model, setModel] = useState(null);

	// Error handler
	const errors = useMemo(() => {
		const errorNew = tabsProps.errors?.includes(getErrorKey(sectionId, "new"));
		const errorPreOwned = tabsProps.errors?.includes(getErrorKey(sectionId, "preOwned"));
		return [errorNew, errorPreOwned];
	}, [sectionId, tabsProps.errors, getErrorKey]);

	// Actions handlers
	const handleUpdateSwitches = (section, area, value) => {
		const updatedTabsProps = { ...model?.tabsProps, [area]: value };
		const update = { ...model, tabsProps: updatedTabsProps };

		setModel(update);

		onActionUpdate?.(section, "switches", {
			globalUnitCounts: updatedTabsProps["globalUnitCounts"],
			applyTiersRetroactively: updatedTabsProps["applyTiersRetroactively"],
		});
	};

	const handleUpdateModels = (section, area, data) => {
		const updatedCommissions = { ...model?.commissions, [area]: data };
		const update = { ...model, commissions: updatedCommissions };
		setModel(update);
		onActionUpdate?.(section, area, data);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	const emptyModelsError = useMemo(() => {
		if (model?.commissions) {
			let error = true;
			for (const [key, value] of Object.entries(model.commissions)) {
				if (key && value?.length) {
					error = false;
					break;
				}
			}
			return { ...emptyError, error };
		}
		return emptyError;
	}, [emptyError, model]);

	useEffect(() => {
		emptyModelsError?.error ? addSectionError(sectionId, "emptyModels") : removeSectionError(sectionId, "emptyModels");
	}, [sectionId, emptyModelsError, addSectionError, removeSectionError]);

	useEffect(() => {
		setModel({
			tabsProps,
			commissions,
		});

		return () => {
			setModel(null);
		};
	}, [tabsProps, commissions]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{props.loading ? (
					<Grid mt={2}>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					!!model?.commissions && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							errors={errors}
							content={Object.entries(model.commissions)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} mb={1} className={"models-tiered-area"}>
										<ModelBased
											key={`${key}_${index}`}
											modelKey={key}
											frontProductLabel={frontProductLabel}
											backProductLabel={backProductLabel}
											frontProducts={frontProducts || []}
											backProducts={backProducts || []}
											makes={makes || []}
											models={value || []}
											emptyError={emptyModelsError}
											loading={loading}
											loadingProducts={loadingProducts}
											loadingMakes={loadingMakes}
											readOnly={readOnly}
											onActionUpdate={(updatedModel) => handleUpdateModels(sectionId, key, updatedModel)}
											onActionValidation={(updatedValidation) => handleValidation(sectionId, key, updatedValidation)}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"globalUnitCounts"}
									textLeft={true}
									textChecked={"Global unit counts"}
									textUnChecked={"Global unit counts"}
									checked={model?.tabsProps.globalUnitCounts}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "globalUnitCounts", value)}
									tooltip={{
										title: "Uses all units of New and Pre-Owned Vehicles together.",
										placement: "bottom",
										key: "model-commissions-global-unit-counts-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={model?.tabsProps.applyTiersRetroactively}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value)}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
ModelBasedCommissions.propTypes = {
	sectionId: PropTypes.string,
	commissions: PropTypes.object,
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	makes: PropTypes.array,
	tabsProps: PropTypes.object,
	frontProductLabel: PropTypes.string,
	backProductLabel: PropTypes.string,
	emptyError: PropTypes.object,
	loading: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
